import { Crumb } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { IGroupAccessOwnerColumnQueryable, IGroupAccessOwnerColumn } from '../../../models/GroupAccessOwners/GroupAccessOwnerColumn';
import { GroupAccessOwnerType } from '../../../models/IGroupAccessOwner';
import { IHrisColumnMapping } from '../../../models/IHrisAttributeModel';
import { DoesPersonHaveGroupAccess } from '../../../utilities/GroupAccessOwners/GroupAccesUtil/groupAccessOwners';
import { getHrisExpectedColumnTypeLabel, GetGroupAccessTypeLabel } from '../../../utilities/GroupAccessOwners/GroupAccesUtil/TextHelpers';

export const AttributeColumn = (attributeMapping: IHrisColumnMapping): IGroupAccessOwnerColumnQueryable => ({
	header: {
		label: getHrisExpectedColumnTypeLabel(attributeMapping.property),
		field: attributeMapping.columnName,
	},

	render: {
		cellData: (row) => row.owner.attributes[attributeMapping.columnName],
	},

	asQueryable: {
		boundToEntity: 'GroupAccessOwner',
		fieldName: `Attributes['${attributeMapping.columnName}']`,
	},
});

export const GroupAccessToColumn = (): IGroupAccessOwnerColumn => ({
	header: {
		label: 'Group Access To',
		field: 'AggregateGroupAccessTo',
	},

	render: {
		cellData: (row: { owner: any }) => {
			const { owner } = row;

			if (DoesPersonHaveGroupAccess(owner)) {
				const accessToHierarchies = Object.keys(owner.owns);
				const accessToNodes = accessToHierarchies.map((h) => owner.owns[h].nodesOwned).flat();

				return `${accessToHierarchies.length} hierarchies, ${accessToNodes.length} nodes`;
			}
		},
	},
});

export const OwnerTypeColumn = (): IGroupAccessOwnerColumnQueryable => ({
	header: {
		label: 'Type',
		field: 'OwnerType',
	},

	render: {
		cellData: (row: { owner: any }) => {
			const { owner } = row;

			if (DoesPersonHaveGroupAccess(owner)) {
				if (owner.ownerType === GroupAccessOwnerType.Employee) {
					return 'Employee';
				} else {
					return 'Collaborator';
				}
			}
		},
	},

	asQueryable: {
		boundToEntity: 'GroupAccessOwner',
		fieldName: 'OwnerType',
	},
});

export const OwnerProfileColumn = (): IGroupAccessOwnerColumnQueryable => ({
	asQueryable: {
		boundToEntity: 'GroupAccessOwner',
		fieldName: 'TiedToProfileId',
	},

	header: {
		label: 'Profile',
		field: 'Profile',
	},

	render: {
		cellData: (row: { owner: any }) => {
			const { owner } = row;

			if (DoesPersonHaveGroupAccess(owner)) {
				return owner.profile.name;
			}
		},
	},
});

export const HierarchyGroupAccessNameColumn = (): IGroupAccessOwnerColumn => ({
	header: {
		label: 'Name',
		field: 'HierarchyName',
	},

	render: {
		cellData: (row) => {
			const { owner, hierarchy } = row;

			if (DoesPersonHaveGroupAccess(owner) && hierarchy !== undefined) {
				return owner.owns[hierarchy].hierarchyName;
			}
		},
	},
});

export const HierarchyGroupAccessTypeColumn = (): IGroupAccessOwnerColumn => ({
	header: {
		label: 'Access Type',
		field: 'GroupAccessType',
	},

	render: {
		cellData: (row) => {
			const { owner, hierarchy } = row;

			if (DoesPersonHaveGroupAccess(owner) && hierarchy !== undefined) {
				const { ownershipAssigned } = owner.owns[hierarchy];
				return GetGroupAccessTypeLabel(ownershipAssigned);
			}
		},
	},
});

export const HierarchyGroupAccessToNodesColumn = (): IGroupAccessOwnerColumnQueryable => ({
	header: {
		label: 'Group Access For Node(s)',
		field: 'GroupAccessForNodes',
	},

	render: {
		cellData: (row) => {
			const { owner, hierarchy } = row;

			if (DoesPersonHaveGroupAccess(owner) && hierarchy !== undefined) {
				return owner.owns[hierarchy].nodesOwned.map((nodeOwned: { nodeName: any; nodeId: string | number | null | undefined }) => {
					return (
						<Crumb
							crumb={{ displayText: nodeOwned.nodeName, value: nodeOwned.nodeId }}
							key={nodeOwned.nodeId}
							onDismiss={() => {
								/**/
							}}
						/>
					);
				});
			}
		},
	},

	asQueryable: {
		boundToEntity: 'NodeOwned',
		fieldName: 'NodeName',
	},
});
